import { NoRankGapError, rankAfter, rankBetween } from '@sbe-spinoff-shared/lexorank';

// rank 계산(rankBetween·rankAfter)은 FE·BE 공용 `@sbe-spinoff-shared/lexorank`가 정본이다. 이 파일에는 서비스 목록 위/아래 이동 로직만 둔다.

export interface RankedItem {
  id: string;
  sortKey: string | null;
}

/**
 * 목록에서 `id` 항목을 한 칸 위/아래로 옮길 때 그 항목이 가져야 할 새 sortKey. **옮길 수 없으면 `null`** 이다 — 이미 맨 위(위로)·맨
 * 아래(아래로)이거나, 새 위치의 이웃 중 sortKey 가 없는(null) 항목이 있거나(없는 쪽을 열린 경계로 쓰면 `[null,null,null]` 같은 목록에서
 * 순서가 정해지지 않는다), 이웃과 값이 같거나 역전돼 있거나, 두 값 사이에 끼울 값이 없는(`NoRankGapError`) 경우다.
 * `items` 는 표시 순서(오름차순)이다. 호출부는 `null` 이면 아무것도 저장하지 않고 위/아래 버튼을 비활성화한다.
 */
export function sortKeyAfterMove(items: readonly RankedItem[], id: string, direction: 'up' | 'down'): string | null {
  const index = items.findIndex((item) => item.id === id);
  if (index < 0) return null;
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= items.length) return null;

  // 옮긴 뒤 위치 기준의 새 이웃: 위로 가면 (target 의 앞, target) 사이, 아래로 가면 (target, target 의 뒤) 사이.
  // 목록 끝이라 이웃이 아예 없는 쪽(undefined)은 열린 경계지만, 이웃이 있는데 sortKey 가 null 이면 경계로 쓸 수 없다.
  const beforeItem = direction === 'up' ? items[targetIndex - 1] : items[targetIndex];
  const afterItem = direction === 'up' ? items[targetIndex] : items[targetIndex + 1];
  // 이웃이 있는데 sortKey 가 없는 경우를 `null` 뿐 아니라 `undefined`(API 응답 누락)에서도 막는다 — 막지 않으면 이웃이 있는데도
  // 열린 경계로 취급돼 순서가 어긋난다. 이웃이 없는 쪽(목록 끝)만 열린 경계다.
  const hasNoKey = (item: RankedItem | undefined): boolean => item !== undefined && (item.sortKey === null || item.sortKey === undefined);
  if (hasNoKey(beforeItem) || hasNoKey(afterItem)) return null;
  const before = beforeItem?.sortKey ?? null;
  const after = afterItem?.sortKey ?? null;
  if (before !== null && after !== null && before >= after) return null;
  try {
    // 맨 아래로 옮길 때(뒤 이웃 없음)는 맨 뒤 붙이기이므로 rankAfter — rankBetween(before, null)은 키가 빨리 길어진다.
    return after === null && before !== null ? rankAfter(before) : rankBetween(before, after);
  } catch (err) {
    if (err instanceof NoRankGapError) return null;
    throw err;
  }
}

/**
 * 목록을 `draftIds` 순서로 재배열했을 때, 순서를 맞추려고 **새 sortKey 를 받아야 하는 항목**과 그 값. 바뀌는 항목을 최소로 하려고 원래 키가 이미
 * 올바른 순서를 이루는 가장 긴 부분(증가 부분수열)은 그대로 두고, 나머지를 앞뒤 고정 항목 사이에 끼운다 — 항목 하나를 끌어 옮기면 보통 하나만 바뀐다.
 * `items` 는 원래 표시 순서, `draftIds` 는 초안 순서(같은 id 집합)이다. 원래 키가 없는(null) 항목은 고정할 수 없어 항상 새 키를 받는다.
 * 사이에 끼울 값이 없으면(`NoRankGapError`) 모든 항목에 새 키를 다시 매긴다. 목록에 없는 id 가 있으면 `null`(호출부가 저장을 포기한다).
 */
export function planSortKeyChanges(items: readonly RankedItem[], draftIds: readonly string[]): ReadonlyMap<string, string> | null {
  const byId = new Map(items.map((item) => [item.id, item] as const));
  if (draftIds.length !== items.length || draftIds.some((id) => !byId.has(id))) return null;
  const keys = draftIds.map((id) => byId.get(id)?.sortKey ?? null);

  // 키가 있는 항목들 중 가장 긴 (엄격) 증가 부분수열 — 이 항목들은 키를 그대로 둔다.
  const best: number[] = [];
  const prev: number[] = [];
  let bestEnd = -1;
  keys.forEach((key, i) => {
    best[i] = 0;
    prev[i] = -1;
    if (key === null) return;
    best[i] = 1;
    for (let j = 0; j < i; j++) {
      const other = keys[j];
      if (other !== null && other < key && best[j]! + 1 > best[i]!) {
        best[i] = best[j]! + 1;
        prev[i] = j;
      }
    }
    if (bestEnd < 0 || best[i]! > best[bestEnd]!) bestEnd = i;
  });
  const stable = new Set<number>();
  for (let i = bestEnd; i >= 0; i = prev[i]!) stable.add(i);

  const changes = new Map<string, string>();
  try {
    let lower: string | null = null;
    for (let i = 0; i < draftIds.length; ) {
      if (stable.has(i)) {
        lower = keys[i]!;
        i++;
        continue;
      }
      let next = i;
      while (next < draftIds.length && !stable.has(next)) next++;
      const upper: string | null = next < draftIds.length ? keys[next]! : null;
      for (let k = i; k < next; k++) {
        // 맨 뒤에 붙일 때(`upper` 없음, 앞 이웃 있음)는 rankAfter — rankBetween(lower, null)은 키가 빨리 길어진다.
        const key: string = upper === null && lower !== null ? rankAfter(lower) : rankBetween(lower, upper);
        changes.set(draftIds[k]!, key);
        lower = key;
      }
      i = next;
    }
    return changes;
  } catch (err) {
    if (!(err instanceof NoRankGapError)) throw err;
  }

  // 틈이 없다 — 모든 항목에 처음부터 다시 매긴다.
  const rebuilt = new Map<string, string>();
  let last: string | null = null;
  for (const id of draftIds) {
    last = last === null ? rankBetween(null, null) : rankAfter(last);
    rebuilt.set(id, last);
  }
  return rebuilt;
}
